import { useQuery } from '@tanstack/react-query';
import { useAtom } from 'jotai';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import { createRef, FocusEventHandler, FunctionComponent } from 'react';
import { UseFormRegisterReturn } from 'react-hook-form';

import {
  brandAtom,
  modelAtom,
  setversionAtom,
} from '../../../../../hooks/use-picker-selection';
import { fetchModels } from '../../../../../services/vehicles';
import Select, {
  CustomDownChevron,
  CustomMenuWithoutMobile,
  CustomOptionModelsVersions,
  CustomSingleValueModelsVersions,
} from '../../../../ui/inputs/custom-selector';

export const modelRef = createRef();

const loadModels = async ({ queryKey: [_, locale, slug] }) => {
  const data = await fetchModels({ locale, slug });

  return Object.entries(data.children).map(([key, value]) => ({
    label: key,
    options: value.map((item) => ({
      value: item.slug,
      label: item.name,
      start_date: item.start_date,
      end_date: item.end_date || new Date().getFullYear(),
      hasVersions: item.have_versions,
    })),
  }));
};

const ModelSelect: FunctionComponent<{
  onFocus?: FocusEventHandler;
  register?: UseFormRegisterReturn;
  error?: Array<string>;
  setValue?: any;
}> = ({ onFocus, register, error, setValue }) => {
  const { locale } = useRouter();
  const { t } = useTranslation();
  const [brand] = useAtom(brandAtom);
  const [model, setModel] = useAtom(modelAtom);
  const [, setVersion] = useAtom(setversionAtom);

  const { data: models, isLoading: isModelsLoading } = useQuery(
    ['search-brands', locale, brand?.value],
    loadModels,
    {
      enabled: !!brand,
    }
  );

  return (
    <Select
      {...register}
      ref={modelRef}
      id="model-input"
      aria-label={t('vehicle-picker:chooseModel')}
      openMenuOnFocus
      value={model}
      isDisabled={!brand}
      isLoading={!!brand && isModelsLoading}
      loadingMessage={() => t('common:loading')}
      options={models}
      components={{
        Option: CustomOptionModelsVersions,
        SingleValue: CustomSingleValueModelsVersions,
        DropdownIndicator: CustomDownChevron,
        MenuList: CustomMenuWithoutMobile,
      }}
      onFocus={onFocus}
      onChange={(item) => {
        setModel(item);
        setVersion(null);
        setValue('slug', { brand: brand, model: item });
      }}
      placeholder={t('vehicle-picker:chooseModel')}
      error={error}
      isClearable
    />
  );
};

export default ModelSelect;
